import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ReactPlayer from 'react-player';
import './DemoSection.css';

const demoVideos = [
  {
    id: 1,
    title: 'RADAR+ In a Nutshell',
    description: 'Learn what RADAR+ is and why it matters in just 60 seconds',
    thumbnail: 'https://picsum.photos/seed/radar1/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Overview',
  },
  {
    id: 2,
    title: 'Onboarding for the Best Experience',
    description: 'Set up your integrations, objectives, and thresholds for maximum ROI',
    thumbnail: 'https://picsum.photos/seed/onboarding/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Getting Started',
  },
  {
    id: 3,
    title: 'Using the Radar',
    description: 'Master reading, interpreting, and acting on live radar signals',
    thumbnail: 'https://picsum.photos/seed/radar2/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Core Features',
  },
  {
    id: 4,
    title: 'Toolbox Overview',
    description: 'Discover powerful utilities like Decision Comparison and Forecasting',
    thumbnail: 'https://picsum.photos/seed/toolbox/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Advanced Features',
  },
  {
    id: 5,
    title: 'Marketplace Overview',
    description: 'Find and inject strategy packs to expand your capabilities',
    thumbnail: 'https://picsum.photos/seed/marketplace/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Marketplace',
  },
  {
    id: 6,
    title: 'Selling on the Marketplace',
    description: 'Learn how to list your own risk/opportunity packs',
    thumbnail: 'https://picsum.photos/seed/selling/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Marketplace',
  },
  {
    id: 7,
    title: 'Execution Dashboard',
    description: 'See how automation flows from alert to plan to assigned task',
    thumbnail: 'https://picsum.photos/seed/dashboard/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Operations',
  },
  {
    id: 8,
    title: 'Signal Digest Mode',
    description: 'Get lightweight summaries via Slack/email for busy executives',
    thumbnail: 'https://picsum.photos/seed/digest/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Features',
  },
  {
    id: 9,
    title: 'Integrations Setup',
    description: 'Connect Asana, Slack, HubSpot, ERP, and more with ease',
    thumbnail: 'https://picsum.photos/seed/integrations/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Setup',
  },
  {
    id: 10,
    title: 'Behind the Scenes: How Signals Work',
    description: 'Understand the AI logic and scoring system',
    thumbnail: 'https://picsum.photos/seed/signals/400/225',
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    category: 'Technical',
  },
];

const VideoCard = ({ video, onVideoClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="demo-card"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      onClick={() => onVideoClick(video)}
    >
      <div className="thumbnail-container">
        <img src={video.thumbnail} alt={video.title} />
        <span className="category-badge">{video.category}</span>
        
        <motion.div
          className="content-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.h3
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: isHovered ? 0 : 20, opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.2 }}
          >
            {video.title}
          </motion.h3>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: isHovered ? 0 : 20, opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            {video.description}
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
};

const DemoSection = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
  };

  return (
    <div className="demo-section" id="demos">
      <motion.div 
        className="demo-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2>Demo Video Hub</h2>
        <p>Learn, explore, and master RADAR+ through our comprehensive video library</p>
      </motion.div>

      <motion.div 
        className="demo-grid"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {demoVideos.map((video) => (
          <VideoCard 
            key={video.id} 
            video={video} 
            onVideoClick={handleVideoClick}
          />
        ))}
      </motion.div>

      {selectedVideo && (
        <motion.div 
          className="video-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseVideo}
        >
          <motion.div 
            className="modal-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="video-wrapper">
              <ReactPlayer
                url={selectedVideo.videoUrl}
                width="100%"
                height="100%"
                controls
                playing
              />
            </div>
            <button className="close-button" onClick={handleCloseVideo}>×</button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default DemoSection; 